import type { ScaleBand } from 'd3-scale';
import type { FairValueHistogramBucket } from '../fairValueHistogramFragment';
import { useCallback } from 'react';
import { getXPosition } from '../utils';

type Props = {
  xBandScale: ScaleBand<number>;
  buckets: FairValueHistogramBucket[];
  onBucketSelected: (bucket: FairValueHistogramBucket | null) => void;
};

export const FairValueHistogramChartInteractiveArea = ({
  xBandScale,
  buckets,
  onBucketSelected,
}: Props) => {
  const handleHover = useCallback(
    (
      e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>
    ) => {
      const x = getXPosition(
        e,
        (e.target as SVGRectElement).getBoundingClientRect()
      );

      const bandWidth = xBandScale.bandwidth();
      const bandPadding = xBandScale.padding();
      const bucketMin = xBandScale.domain().find((value) => {
        const bandStart = xBandScale(value) ?? 0;

        return x >= bandStart && x <= bandStart + bandWidth + bandPadding;
      });

      const bucket = buckets.find((b) => {
        return b.min === bucketMin;
      });

      onBucketSelected(bucket ?? null);
    },
    [xBandScale, buckets, onBucketSelected]
  );

  const resetSelection = () => onBucketSelected(null);

  return (
    <rect
      aria-label="Interactive area"
      role="presentation"
      x="0"
      y="0"
      width="100%"
      height="100%"
      fill="transparent"
      className="cursor-pointer touch:cursor-default"
      onMouseMove={handleHover}
      onTouchMove={handleHover}
      onMouseLeave={resetSelection}
      onTouchEnd={resetSelection}
    />
  );
};
